/**
 * The open dialogs of a page, in the order they opened. Only the topmost owns focus and Tab: a dialog
 * beneath another neither pulls focus back into itself nor traps Tab until it is on top again, so two open
 * dialogs never fight over focus. Entries are compared by identity.
 */

/**
 * A stack of open dialogs.
 */
export interface DialogStack<T> {
  /**
   * Puts a dialog on top.
   *
   * @param entry The dialog.
   * @returns A function that takes it off the stack wherever it is, since sibling dialogs that close
   *   together leave in the order React unmounts them; calling it again does nothing.
   */
  open(entry: T): () => void;
  /**
   * Whether a dialog is the topmost open one.
   *
   * @param entry The dialog.
   * @returns True when it is on top.
   */
  isTop(entry: T): boolean;
  /**
   * The topmost open dialog.
   *
   * @returns The dialog, or undefined when none is open.
   */
  top(): T | undefined;
}

/**
 * Makes an empty stack of open dialogs.
 *
 * @returns The stack.
 * @example
 * const stack = createDialogStack<string>();
 * const leave = stack.open("confirm");
 * stack.isTop("confirm");
 * // true
 * leave();
 */
export function createDialogStack<T>(): DialogStack<T> {
  const entries: T[] = [];
  return {
    /**
     * Puts a dialog on top.
     *
     * @param entry The dialog.
     * @returns The function that takes it off the stack.
     */
    open(entry) {
      entries.push(entry);
      let open = true;
      /**
       * Takes the dialog off the stack, once.
       */
      const leave = (): void => {
        if (!open) {
          return;
        }
        open = false;
        const index = entries.lastIndexOf(entry);
        if (index !== -1) {
          entries.splice(index, 1);
        }
      };
      return leave;
    },
    /**
     * Whether a dialog is the topmost open one.
     *
     * @param entry The dialog.
     * @returns True when it is on top.
     */
    isTop(entry) {
      return entries.length > 0 && entries[entries.length - 1] === entry;
    },
    /**
     * The topmost open dialog.
     *
     * @returns The dialog, or undefined when none is open.
     */
    top() {
      return entries.at(-1);
    },
  };
}
